import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BadgeCheck, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { FadeUp } from "../components/motion";
import { SEO } from "../components/SEO";

// Page publique d'avis par LIEN MAGIQUE (courriel post-livraison) : pas de connexion
// requise — le jeton signé prouve l'achat. L'avis reste modéré avant publication.
export default function AvisMagique() {
  const { token } = useParams();
  const { lang, t, ts } = useLang();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/avis-magique/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Lien invalide"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 10) return;
    setBusy(true);
    try {
      await api.post("/avis-magique", { token, rating, text: text.trim() });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const productName = info ? (lang === "fr" ? info.product_name : (info.product_name_en || info.product_name)) : "";

  return (
    <div className="pt-32 pb-28 min-h-screen" data-testid="avis-magique-page">
      <SEO title={ts("magicReview.seo_title")} description={ts("magicReview.seo_title")} path="/avis" />
      <div className="max-w-xl mx-auto px-6">
        {error ? (
          <FadeUp className="text-center bg-surface border border-ivory/15 rounded-2xl p-10">
            <p className="text-ivory/70 mb-6" data-testid="magic-error">{error}</p>
            <Link to="/" className="text-gold hover:underline">{t("magicReview.home")}</Link>
          </FadeUp>
        ) : !info ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
        ) : done || info.already ? (
          <FadeUp className="text-center bg-surface border border-gold/25 rounded-2xl p-10" data-testid="magic-thanks">
            <BadgeCheck className="w-12 h-12 text-gold mx-auto mb-4" />
            <h1 className="font-serif text-3xl mb-3">{t("magicReview.thanks_title")}</h1>
            <p className="text-ivory/60 mb-6">{t("magicReview.thanks_text")}</p>
            <Link to="/boutique" className="inline-flex bg-gold text-ink font-bold px-7 py-3 rounded-full text-sm hover:bg-champagne transition-colors">
              {t("magicReview.back_shop")}
            </Link>
          </FadeUp>
        ) : (
          <FadeUp className="bg-surface border border-gold/25 rounded-2xl p-8 md:p-10">
            <p className="text-[11px] uppercase tracking-widest text-gold mb-2">{t("magicReview.kicker")}</p>
            <h1 className="font-serif text-3xl mb-1">{productName}</h1>
            <p className="text-ivory/50 text-xs mb-7">{t("magicReview.sub")} · {t("productReviews.verified")}</p>
            {info.image && (
              <img src={info.image} alt={productName} className="w-24 h-24 object-contain rounded-lg bg-ink border border-ivory/10 mb-6" />
            )}
            <form onSubmit={submit}>
              <div className="flex justify-center gap-1.5 mb-6">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setRating(n)} data-testid={`magic-star-${n}`}
                    className="hover:scale-110 transition-transform">
                    <Star size={34} className={n <= rating ? "fill-[#d4af37] text-gold" : "text-ivory/25"} />
                  </button>
                ))}
              </div>
              <textarea
                value={text} onChange={(e) => setText(e.target.value)}
                placeholder={ts("magicReview.placeholder")} rows={5} maxLength={1000}
                data-testid="magic-text"
                className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none mb-4"
              />
              <button type="submit" disabled={busy || text.trim().length < 10}
                data-testid="magic-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold py-4 rounded-full text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.35)] transition-all duration-300 disabled:opacity-50">
                {busy ? <Loader2 size={15} className="animate-spin" /> : null}
                {busy ? t("productReviews.sending") : t("magicReview.send")}
              </button>
              <p className="text-[11px] text-ivory/45 mt-3 text-center">{t("productReviews.moderation_note")}</p>
            </form>
          </FadeUp>
        )}
      </div>
    </div>
  );
}
